import { Stack } from '@mui/material';
import { useAppSelector } from 'core/store/hooks';
import { useMemo } from 'react';
import { BORDER_COLOR } from 'utils/theme';
import useSourceTypes from '../hooks/useSourceTypes';
import Summary from '../Summary';
import ClaimEvidence from './components/ClaimEvidence';
import ClaimSummary from './components/ClaimSummary';
import RephrasedClaims from './components/ClaimSummary/RephrasedClaims/RephrasedClaims';
import { getClaimKeys } from './helper';

interface ClaimProps {
  claim: Claim;
  claimIndex: number;
  expandedClaim: string | null;
  setExpandedClaim: (value: string | null) => void;
}

const Claim = ({ claim, claimIndex, expandedClaim, setExpandedClaim }: ClaimProps) => {
  const selectedSortingAccordion = useAppSelector(
    (state) => state.sourceFilterV2.selectedSortingAccordion
  );
  const favoriteDomains = useAppSelector((state) => state.claimsV2.favoriteDomains);
  const claimLoading = useAppSelector((state) => state.claimsV2.isClaimLoading);

  const { groupedSourceTypes } = useSourceTypes(claim.evidence ?? []);
  const sortedOrFilteredSources = useMemo(() => {
    const sources = groupedSourceTypes[selectedSortingAccordion] || [];

    return sources.slice().sort((sourceA, sourceB) => {
      const isSourceAFavorite = (favoriteDomains || []).includes(sourceA.domain);
      const isSourceBFavorite = (favoriteDomains || []).includes(sourceB.domain);
      if (isSourceAFavorite === isSourceBFavorite) {
        return 0;
      } else if (isSourceAFavorite) {
        return -1;
      } else {
        return 1;
      }
    });
  }, [groupedSourceTypes, selectedSortingAccordion, favoriteDomains]);
  const { summaryKey, rephraseKey } = getClaimKeys(claim.claim);

  const isSourceOpen = expandedClaim === claim.claim;
  const isRephraseOpen = expandedClaim === rephraseKey;
  const isSummaryOpen = expandedClaim === summaryKey;

  return (
    <Stack display="flex" direction="column">
      <ClaimSummary
        claim={claim}
        claimIndex={claimIndex}
        setExpanded={setExpandedClaim}
        expandedClaim={expandedClaim}
      />

      <Stack
        sx={{
          borderTop: `1px solid ${BORDER_COLOR}`,
          display: isSourceOpen ? 'block' : 'none',
        }}
      >
        {sortedOrFilteredSources.map((source, sourceIndex) => (
          <ClaimEvidence
            sourceIndex={sourceIndex}
            claimIndex={claimIndex}
            evidence={source}
            key={sourceIndex}
            claim={claim}
            isLastEvidenceIndex={sortedOrFilteredSources.length - 1}
          />
        ))}
      </Stack>

      <Stack
        sx={{
          borderTop: `1px solid ${BORDER_COLOR}`,
          display: isSummaryOpen ? 'block' : 'none',
        }}
      >
        <Summary
          useCardWrapper={false}
          isLoading={claimLoading}
          claim={claim}
          setExpandedClaim={setExpandedClaim}
        />
      </Stack>

      {isRephraseOpen && (
        <Stack sx={{ mt: 3 }}>
          <RephrasedClaims claim={claim} />
        </Stack>
      )}
    </Stack>
  );
};

export default Claim;
